import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const MEETING_URL = process.env.REACT_APP_HUBSPOT_MEETING_LINK_URL;

const NAV_LINKS = [
  { to: "/xcellereyes", label: "XcellerEYES" },
  { to: "/resources", label: "Clinical Resources" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-9 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#101828]/85 border-b border-teal/15 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img
            src="/brand/renvia-horizontal-white-transparent.png"
            alt="RenVia"
            className="h-9 md:h-10 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-teal" : "text-white/85 hover:text-teal"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          {MEETING_URL ? (
            <a
              href={MEETING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("request_demo_click", { location: "nav-desktop" })}
              data-testid="nav-cta-consult"
              className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal2 transition-colors shadow-[0_10px_30px_-10px_rgba(21,184,184,0.6)]"
            >
              Schedule a Consultation
              <span aria-hidden>→</span>
            </a>
          ) : (
            <Link
              to="/contact"
              onClick={() => trackEvent("request_demo_click", { location: "nav-desktop" })}
              data-testid="nav-cta-consult"
              className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal2 transition-colors shadow-[0_10px_30px_-10px_rgba(21,184,184,0.6)]"
            >
              Schedule a Consultation
              <span aria-hidden>→</span>
            </Link>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2 rounded-md hover:bg-white/10"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden backdrop-blur-xl bg-[#101828]/95 border-t border-teal/15"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base font-medium py-3 border-b border-white/5 ${
                    isActive ? "text-teal" : "text-white/90"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            {MEETING_URL ? (
              <a
                href={MEETING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("request_demo_click", { location: "nav-mobile" })}
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-teal px-5 py-3 text-sm font-semibold text-white"
              >
                Schedule a Consultation
              </a>
            ) : (
              <Link
                to="/contact"
                onClick={() => trackEvent("request_demo_click", { location: "nav-mobile" })}
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-teal px-5 py-3 text-sm font-semibold text-white"
              >
                Schedule a Consultation
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
